import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Copy, ExternalLink, QrCode, Mail } from "lucide-react";
import { sendTemplatedEmail } from "@/services/emailApi";
import { useEmailTemplateOverride } from "@/hooks/useEmailTemplateOverrides";

interface EnrollDeviceDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Public enrolment URL the QR code encodes. Uses the production
 * console domain by default and falls back to the current origin
 * for previews so the QR is always scannable from where it was
 * generated.
 */
const ENROL_PATH = "/enrol/device";
const DEFAULT_ENROL_ORIGIN = "https://console.smplify.com";

function getEnrolUrl(): string {
  if (typeof window === "undefined") return `${DEFAULT_ENROL_ORIGIN}${ENROL_PATH}`;
  const { origin, hostname } = window.location;
  // On the production console domain prefer the canonical URL; on
  // every other host (preview, localhost) use the current origin so
  // testers can scan the QR with their phone on the same network.
  if (hostname === "console.smplify.com") {
    return `${DEFAULT_ENROL_ORIGIN}${ENROL_PATH}`;
  }
  return `${origin}${ENROL_PATH}`;
}

export const EnrollDeviceDialog = ({ isOpen, onClose }: EnrollDeviceDialogProps) => {
  const enrolUrl = getEnrolUrl();
  const [inviteEmail, setInviteEmail] = useState("");
  const [enrollmentUrl, setEnrollmentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Per-tenant override configured under Settings → Email Templates.
  // When set, we forward `custom_subject` / `custom_body` so /api/v1/emails/send
  // uses the admin-customised copy instead of the backend's default.
  const inviteOverride = useEmailTemplateOverride("INVITE_USER");

  const handleEnroll = async () => {
    try {
      setIsLoading(true);
      // Send via the generic templated-email endpoint so the message
      // honours the tenant's configured SMTP settings and INVITE_USER
      // template (subject, body, branding).
      await sendTemplatedEmail({
        recipients: [inviteEmail],
        template_type: "INVITE_USER",
        variables: {
          enrol_link: enrolUrl,
          invite_link: enrolUrl,
        },
        // Spread only the fields the admin actually customised — leaving
        // them undefined lets the backend fall back to the default template.
        ...(inviteOverride?.custom_subject
          ? { custom_subject: inviteOverride.custom_subject }
          : {}),
        ...(inviteOverride?.custom_body
          ? { custom_body: inviteOverride.custom_body }
          : {}),
      });
      setEnrollmentUrl(enrolUrl);
      toast.success("Enrollment invitation sent");
    } catch (error: any) {
      console.error("Enrollment error:", error);
      toast.error(error?.message || "Failed to send invitation");
    } finally {
      setIsLoading(false);
    }
  };

  const copyEnrolUrl = async () => {
    try {
      await navigator.clipboard.writeText(enrolUrl);
      toast.success("Enrollment link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enroll New Device</DialogTitle>
          <DialogDescription>
            Share the QR code with the end user, or send them an enrollment
            invitation by email.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="qr" className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="qr">
              <QrCode className="mr-2 h-4 w-4" />
              QR code
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail className="mr-2 h-4 w-4" />
              Email invite
            </TabsTrigger>
          </TabsList>

          <TabsContent value="qr" className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              The user scans this code on the device they want to enrol. It
              opens the public enrolment page where they sign in with the
              credentials you provided.
            </p>
            <div className="flex justify-center rounded-lg border border-border bg-card p-4">
              <QRCodeSVG
                value={enrolUrl}
                size={208}
                level="H"
                includeMargin
                aria-label={`QR code for ${enrolUrl}`}
                imageSettings={{
                  src: "/lovable-uploads/smplify-logo-symbol.png",
                  // Square symbol mark — ~20% of the QR side, comfortably
                  // within the ~30% redundancy budget of error-correction
                  // level H so the code stays scannable with the logo on top.
                  height: 44,
                  width: 44,
                  // Punch a small white hole behind the logo so the
                  // surrounding modules aren't visually crowded.
                  excavate: true,
                }}
              />
            </div>
            <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Enrollment link
              </Label>
              <a
                href={enrolUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block break-all text-sm font-medium text-primary hover:underline"
              >
                {enrolUrl}
              </a>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={copyEnrolUrl}>
                <Copy className="mr-2 h-4 w-4" />
                Copy link
              </Button>
              <Button asChild type="button">
                <a href={enrolUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open page
                </a>
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="email" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                autoComplete="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@company.com"
              />
            </div>
            {enrollmentUrl && (
              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-sm font-medium">Enrollment URL</Label>
                <p className="text-sm mt-1 break-all text-muted-foreground">
                  {enrollmentUrl}
                </p>
              </div>
            )}
            <Button
              onClick={handleEnroll}
              disabled={isLoading || !inviteEmail}
              className="w-full"
            >
              {isLoading ? "Sending..." : "Send enrolment invitation"}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
